import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { previewTransactionDescriptions, updateTransactionDescriptions } from '../services/api';

export default function BulkTransactionRenameDialog({ open, transactions, onClose, onRenamed }) {
  const [merchant, setMerchant] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const selection = transactions.map(t => t.id).sort((a, b) => a - b).join(',');
  useEffect(() => {
    generation.current += 1;
    setMerchant(''); setPreview(null); setError(''); setBusy(false);
    return () => { generation.current += 1; };
  }, [open, selection]);
  const request = { transaction_ids: transactions.map(t => t.id), merchant: merchant.trim() };
  const showPreview = async () => {
    const ticket = generation.current;
    setBusy(true); setError(''); setPreview(null);
    try {
      const result = await previewTransactionDescriptions(request);
      if (ticket === generation.current) setPreview(result.data);
    } catch (err) {
      if (ticket === generation.current) setError(err.response?.data?.msg || 'Could not preview. No changes were saved.');
    } finally { if (ticket === generation.current) setBusy(false); }
  };
  const save = async () => {
    const ticket = generation.current;
    setBusy(true); setError('');
    try {
      const result = await updateTransactionDescriptions({ ...request, fingerprint: preview.fingerprint });
      if (ticket === generation.current) onRenamed(result.data.changes);
    } catch (err) {
      if (ticket === generation.current) {
        setPreview(null);
        setError(err.response?.data?.msg || 'Save could not be confirmed. Refresh to check the descriptions before retrying.');
      }
    } finally { if (ticket === generation.current) setBusy(false); }
  };
  return <Dialog open={open} onClose={() => { if (!busy) onClose(); }} fullWidth maxWidth="sm">
    <DialogTitle>Edit merchant / description</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Typography>{transactions.length} selected. Only descriptions change. Amounts, dates, accounts and categories stay unchanged.</Typography>
        <Typography variant="body2">Generated split suffixes are preserved. Other descriptions are replaced in full.</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField autoFocus fullWidth label="New merchant / description" value={merchant} disabled={busy}
          inputProps={{ maxLength: 200 }} onChange={event => { setMerchant(event.target.value); setPreview(null); setError(''); }} />
        {preview && <Box aria-label="Description change preview">
          {preview.changes.map(change => <Box key={change.id} sx={{ mb: 2, overflowWrap: 'anywhere' }}>
            <Typography variant="caption">{change.transaction_date} · {change.category_name || 'No category'} · ${Math.abs(Number(change.amount)).toFixed(2)}</Typography>
            <Typography variant="body2">Before: {change.before || '(empty)'}</Typography>
            <Typography variant="body2" fontWeight="bold">After: {change.after}</Typography>
          </Box>)}
        </Box>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
      <Button disabled={busy} onClick={onClose}>Cancel</Button>
      <Button disabled={busy || !merchant.trim()} onClick={showPreview}>Preview changes</Button>
      <Button variant="contained" disabled={busy || !preview} onClick={save}>Save {transactions.length} descriptions</Button>
    </DialogActions>
  </Dialog>;
}
